<script setup lang="ts">
import { computed } from 'vue';

import type { PlaybookRunInDb, VaultFileRevisionInDb } from '@/api';
import { runInputText } from '@/services/playbooks/playbookRunDisplay';

const props = defineProps<{
  run: PlaybookRunInDb;
}>();

const emit = defineEmits<{
  'view-text': [text: string];
  'view-file': [revision: VaultFileRevisionInDb];
}>();

const rawInput = computed<string | null>(() => runInputText(props.run));
const revision = computed<VaultFileRevisionInDb | null>(
  () => props.run.input_vault_file_revision ?? null
);

const textPreview = computed<string>(() => {
  const text = rawInput.value ?? '';
  const lines = text.split('\n');
  const head = lines.slice(0, 5).join('\n');
  return lines.length > 5 ? `${head}\n…` : head;
});

const ownerName = computed<string>(() => {
  const user = revision.value?.created_by_user;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
  return user?.full_name || user?.email || '—';
});

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
</script>

<template>
  <n-popover v-if="rawInput" trigger="hover" placement="top-start" :width="360">
    <template #trigger>
      <n-button text type="primary" @click.stop="emit('view-text', rawInput)">Text</n-button>
    </template>
    <pre
      style="
        margin: 0;
        white-space: pre-wrap;
        word-break: break-word;
        font-size: 12px;
        line-height: 1.5;
      "
      >{{ textPreview }}</pre>
  </n-popover>

  <n-popover v-else-if="revision" trigger="hover" placement="top-start" :width="320">
    <template #trigger>
      <n-button text type="primary" @click.stop="emit('view-file', revision)">
        <n-ellipsis :tooltip="false" style="max-width: 150px">{{ revision.file_name }}</n-ellipsis>
      </n-button>
    </template>
    <n-descriptions :column="1" size="small" label-placement="left">
      <n-descriptions-item label="Name">{{ revision.file_name }}</n-descriptions-item>
      <n-descriptions-item label="Created">{{
        formatDate(revision.created_at)
      }}</n-descriptions-item>
      <n-descriptions-item label="Owner">{{ ownerName }}</n-descriptions-item>
    </n-descriptions>
  </n-popover>

  <span v-else>—</span>
</template>
